// Risk lists: stock-outs, low stock, excess inventory for this run.
//
// Rows are ONE PER SKU, because that is the grain the decision is made at —
// reorder, transfer, discount. Opening a row reveals the locations behind it
// (with a hub/spoke marker for hub-and-spoke customers) and, for multi-channel
// customers, the SKU's month-forward demand split by channel. Single-channel
// customers get no split: it would only restate the SKU's own demand.
//
// Aggregation across a SKU's locations is per-measure: money and units add up,
// exposure measures (weeks out, days to stockout) take the worst location, and
// ratios like months of supply are recomputed from the summed parts rather
// than averaged.
//
// For hub-and-spoke customers the stock-out table carries a final "Hub can
// cover" column — units of that SKU available at the hub — which is what turns
// a stock-out list into an action list. Omitted entirely for flat networks.
import { Fragment, ReactNode, useState } from "react";
import {
  SnapshotExcessRow,
  SnapshotLowStockRow,
  SnapshotRiskRow,
  SnapshotStockOutRow,
} from "./types";
import {
  EM_DASH,
  LinkVal,
  RoleDot,
  SectionTitle,
  SnapCard,
  un,
  usd,
  useSnapshotTokens,
} from "./primitives";

type Tone = "red" | "orange";

const RiskTable = ({
  tone,
  title,
  count,
  subtotal,
  linkPath,
  linkLabel,
  columns,
  children,
}: {
  tone: Tone;
  title: string;
  count: number;
  subtotal?: string;
  linkPath: string;
  linkLabel: string;
  columns: { label: string; align?: "right" }[];
  children: ReactNode;
}) => {
  const t = useSnapshotTokens();
  const tones =
    tone === "red"
      ? { bg: t.red1, ink: t.redInk }
      : { bg: t.orange1, ink: t.orangeInk };
  return (
    <SnapCard pad={0} style={{ overflow: "hidden" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "11px 16px",
          borderBottom: `1px solid ${t.line}`,
          background: t.surface2,
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 2,
            background: tones.ink,
          }}
        />
        <span style={{ fontSize: 13, fontWeight: 700, color: t.ink }}>
          {title}
        </span>
        <span
          style={{
            padding: "1px 8px",
            borderRadius: 999,
            background: tones.bg,
            color: tones.ink,
            border: `1px solid ${tones.ink}40`,
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {count}
        </span>
        <div style={{ flex: 1 }} />
        {subtotal && (
          <span style={{ fontSize: 12, color: t.ink3 }}>{subtotal}</span>
        )}
        <LinkVal to={linkPath} label={linkLabel}>
          <span style={{ fontSize: 11.5, color: t.blue, fontWeight: 600 }}>
            {linkLabel}
          </span>
        </LinkVal>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th
                key={i}
                style={{
                  textAlign: c.align || "left",
                  fontSize: 10.5,
                  fontWeight: 600,
                  letterSpacing: 0.4,
                  textTransform: "uppercase",
                  color: t.ink4,
                  padding: "10px 10px 7px",
                  borderBottom: `1px solid ${t.line}`,
                  whiteSpace: "nowrap",
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </SnapCard>
  );
};

const useTd = (): React.CSSProperties => {
  const t = useSnapshotTokens();
  return {
    fontSize: 12,
    color: t.ink2,
    padding: "8px 10px",
    borderBottom: `1px solid ${t.line2}`,
    whiteSpace: "nowrap",
  };
};

/** One SKU and every location it is flagged at. The tables group by SKU
 *  because the decision — reorder, transfer, discount — is made per SKU;
 *  the location split is the detail behind that decision, not the headline. */
type SkuGroup<R extends SnapshotRiskRow> = {
  sku: string;
  description: string | null;
  rows: R[];
  /** Channel forecast summed across this SKU's flagged locations; null when
   *  no location carried one. */
  channels: { channel: string; units: number }[] | null;
};

const groupBySku = <R extends SnapshotRiskRow>(rows: R[]): SkuGroup<R>[] => {
  const map = new Map<string, SkuGroup<R>>();
  for (const r of rows) {
    const g: SkuGroup<R> = map.get(r.sku) ?? {
      sku: r.sku,
      description: r.description,
      rows: [],
      channels: null,
    };
    g.rows.push(r);
    // Alert rows carry a description, computed fallbacks do not — keep the
    // first non-null so a mixed group still names the product.
    if (!g.description && r.description) {
      g.description = r.description;
    }
    map.set(r.sku, g);
  }
  for (const g of map.values()) {
    const merged = new Map<string, number>();
    let any = false;
    for (const r of g.rows) {
      if (!r.channelForecast) {
        continue;
      }
      any = true;
      for (const c of r.channelForecast) {
        merged.set(c.channel, (merged.get(c.channel) ?? 0) + c.units);
      }
    }
    g.channels = any
      ? [...merged.entries()]
          .map(([channel, units]) => ({ channel, units }))
          .sort((a, b) => b.units - a.units)
      : null;
  }
  return [...map.values()];
};

/** Caret + SKU, the first cell of every group row. */
const SkuGroupCell = ({
  group,
  open,
  onToggle,
}: {
  group: SkuGroup<SnapshotRiskRow>;
  open: boolean;
  onToggle: () => void;
}) => {
  const t = useSnapshotTokens();
  const td = useTd();
  const n = group.rows.length;
  return (
    <td style={{ ...td, padding: 0 }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-label={`${group.sku}: show the ${n} flagged ${
          n === 1 ? "location" : "locations"
        }`}
        style={{
          appearance: "none",
          border: "none",
          background: "transparent",
          cursor: "pointer",
          font: "inherit",
          textAlign: "left",
          width: "100%",
          padding: "8px 10px",
          display: "flex",
          alignItems: "flex-start",
          gap: 7,
        }}
      >
        <span
          aria-hidden
          style={{
            fontSize: 9,
            lineHeight: "16px",
            color: t.ink4,
            transform: open ? "rotate(90deg)" : "none",
            transition: "transform 120ms",
          }}
        >
          ▶
        </span>
        <span style={{ minWidth: 0 }}>
          <span
            style={{ display: "block", fontWeight: 600, color: t.ink }}
          >
            {group.sku}
          </span>
          {group.description && (
            <span
              style={{ display: "block", fontSize: 11, color: t.ink4 }}
            >
              {group.description}
            </span>
          )}
        </span>
      </button>
    </td>
  );
};

/** Locations count cell — the second column of every grouped table. */
const LocationCountCell = ({ group }: { group: SkuGroup<SnapshotRiskRow> }) => {
  const t = useSnapshotTokens();
  const td = useTd();
  const n = group.rows.length;
  return (
    <td style={{ ...td, color: t.ink3 }}>
      {n} {n === 1 ? "location" : "locations"}
    </td>
  );
};

/** The per-location split behind one SKU row, shown when the caret is open. */
const LocationBreakdownRow = <R extends SnapshotRiskRow>({
  group,
  colSpan,
  isHubAndSpoke,
  headers,
  cells,
}: {
  group: SkuGroup<R>;
  colSpan: number;
  isHubAndSpoke: boolean;
  headers: { label: string; align?: "right" }[];
  /** Cells after the location column, in the same order as `headers`. */
  cells: (row: R) => ReactNode;
}) => {
  const t = useSnapshotTokens();
  return (
    <tr>
      <td
        colSpan={colSpan}
        style={{
          padding: "10px 10px 12px 34px",
          background: t.surface2,
          borderBottom: `1px solid ${t.line2}`,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            color: t.ink4,
            marginBottom: 6,
          }}
        >
          By location
        </div>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              {[{ label: "Location" }, ...headers].map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: h.align || "left",
                    fontSize: 10,
                    fontWeight: 600,
                    color: t.ink4,
                    padding: "0 8px 5px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {group.rows.map((r) => (
              <tr key={r.location}>
                <td
                  style={{
                    fontSize: 11.5,
                    color: t.ink2,
                    padding: "4px 8px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {isHubAndSpoke && <RoleDot role={r.role} />}
                    {r.location}
                  </span>
                </td>
                {cells(r)}
              </tr>
            ))}
          </tbody>
        </table>
      </td>
    </tr>
  );
};

/** Small right-aligned cell used inside the per-location breakdown. */
const DetailCell = ({
  children,
  bold,
  color,
}: {
  children: ReactNode;
  bold?: boolean;
  color?: string;
}) => {
  const t = useSnapshotTokens();
  return (
    <td
      style={{
        fontSize: 11.5,
        color: color ?? t.ink2,
        fontWeight: bold ? 600 : 400,
        padding: "4px 8px",
        textAlign: "right",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </td>
  );
};

const DISPLAY_CAP = 30;

const CapNote = ({ total, sortedBy }: { total: number; sortedBy: string }) => {
  const t = useSnapshotTokens();
  if (total <= DISPLAY_CAP) {
    return null;
  }
  return (
    <tr>
      <td
        colSpan={99}
        style={{
          fontSize: 11,
          color: t.ink4,
          padding: "8px 10px",
          textAlign: "center",
        }}
      >
        Showing top {DISPLAY_CAP} of {total} (sorted by {sortedBy})
      </td>
    </tr>
  );
};

export const StockOutTable = ({
  rows,
  isHubAndSpoke,
  linkPath,
  showChannels,
}: {
  rows: SnapshotStockOutRow[];
  isHubAndSpoke: boolean;
  linkPath: string;
  showChannels: boolean;
}) => {
  const t = useSnapshotTokens();
  const td = useTd();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  // Aggregate per SKU: revenue and demand add up across locations; weeks-out
  // takes the worst location, since that is what the SKU is exposed to.
  const groups = groupBySku(rows)
    .map((g) => {
      const revenueAtRisk = g.rows.reduce(
        (a: number | null, r) =>
          r.revenueAtRisk != null ? (a ?? 0) + r.revenueAtRisk : a,
        null as number | null
      );
      const weeksOut = g.rows.reduce(
        (a: number | null, r) =>
          r.weeksOut != null ? Math.max(a ?? 0, r.weeksOut) : a,
        null as number | null
      );
      return {
        g,
        revenueAtRisk,
        weeksOut,
        recentDemand: g.rows.reduce((a, r) => a + r.recentDemand, 0),
        hubCanCover:
          g.rows.find((r) => r.hubCanCover != null)?.hubCanCover ?? null,
      };
    })
    .sort(
      (a, b) =>
        (b.revenueAtRisk ?? 0) - (a.revenueAtRisk ?? 0) ||
        b.recentDemand - a.recentDemand
    );
  const shown = groups.slice(0, DISPLAY_CAP);
  const atRiskTotal = rows.reduce(
    (a: number | null, r) =>
      r.revenueAtRisk != null ? (a ?? 0) + r.revenueAtRisk : a,
    null as number | null
  );
  const subtotal = atRiskTotal == null ? null : usd(atRiskTotal);
  const columns: { label: string; align?: "right" }[] = [
    { label: "SKU" },
    { label: "Locations" },
    { label: "Worst wks out", align: "right" },
    { label: "Recent demand", align: "right" },
    { label: "Rev at risk", align: "right" },
  ];
  if (isHubAndSpoke) {
    columns.push({ label: "Hub can cover", align: "right" });
  }
  return (
    <RiskTable
      tone="red"
      title="Stock-outs"
      count={rows.length}
      subtotal={
        rows.length && subtotal != null
          ? subtotal + " revenue at risk"
          : undefined
      }
      linkPath={linkPath}
      linkLabel="Inventory Health"
      columns={columns}
    >
      {shown.map((a) => {
        const open = expanded.has(a.g.sku);
        return (
          <Fragment key={a.g.sku}>
            <tr>
              <SkuGroupCell
                group={a.g}
                open={open}
                onToggle={() => toggle(a.g.sku)}
              />
              <LocationCountCell group={a.g} />
              <td
                style={{
                  ...td,
                  textAlign: "right",
                  fontWeight: 600,
                  color: t.redInk,
                }}
                title={
                  a.weeksOut == null
                    ? "Weeks-out history needs the run comparison summary"
                    : undefined
                }
              >
                {a.weeksOut ?? EM_DASH}
              </td>
              <td style={{ ...td, textAlign: "right" }}>
                {un(a.recentDemand)}
              </td>
              <td
                style={{ ...td, textAlign: "right", fontWeight: 600 }}
                title={
                  a.revenueAtRisk == null
                    ? "Revenue at risk needs the run comparison summary"
                    : undefined
                }
              >
                {a.revenueAtRisk == null ? EM_DASH : usd(a.revenueAtRisk, 0)}
              </td>
              {isHubAndSpoke && (
                <td
                  style={{
                    ...td,
                    textAlign: "right",
                    color: a.hubCanCover ? t.greenInk : t.ink4,
                    fontWeight: a.hubCanCover ? 600 : 400,
                  }}
                  title={
                    a.hubCanCover == null
                      ? "Hub availability for this SKU is not in the run summary"
                      : undefined
                  }
                >
                  {a.hubCanCover == null
                    ? EM_DASH
                    : a.hubCanCover > 0
                    ? un(a.hubCanCover) + " u"
                    : "none"}
                </td>
              )}
            </tr>
            {open && (
              <>
                <LocationBreakdownRow
                  group={a.g}
                  colSpan={columns.length}
                  isHubAndSpoke={isHubAndSpoke}
                  headers={[
                    { label: "Wks out", align: "right" },
                    { label: "Recent demand", align: "right" },
                    { label: "Rev at risk", align: "right" },
                  ]}
                  cells={(r) => (
                    <>
                      <DetailCell bold color={t.redInk}>
                        {r.weeksOut ?? EM_DASH}
                      </DetailCell>
                      <DetailCell>{un(r.recentDemand)}</DetailCell>
                      <DetailCell>
                        {r.revenueAtRisk == null
                          ? EM_DASH
                          : usd(r.revenueAtRisk, 0)}
                      </DetailCell>
                    </>
                  )}
                />
                {showChannels && (
                  <ChannelForecastPanel
                    rows={a.g.channels}
                    colSpan={columns.length}
                  />
                )}
              </>
            )}
          </Fragment>
        );
      })}
      <CapNote total={groups.length} sortedBy="revenue at risk, then demand" />
      {!rows.length && (
        <tr>
          <td style={{ ...td, color: t.ink4 }} colSpan={columns.length}>
            No stock-outs in this run
          </td>
        </tr>
      )}
    </RiskTable>
  );
};

/** Month-forward forecast split by channel, shown under an expanded row. */
const ChannelForecastPanel = ({
  rows,
  colSpan,
}: {
  rows: { channel: string; units: number }[] | null;
  colSpan: number;
}) => {
  const t = useSnapshotTokens();
  if (!rows?.length) {
    return (
      <tr>
        <td
          colSpan={colSpan}
          style={{
            fontSize: 11.5,
            color: t.ink4,
            padding: "10px 10px 12px 34px",
            background: t.surface2,
            borderBottom: `1px solid ${t.line2}`,
          }}
        >
          {rows == null
            ? "No channel forecast available for this run"
            : "No forecast demand in the next 4 weeks"}
        </td>
      </tr>
    );
  }
  const total = rows.reduce((a, r) => a + r.units, 0);
  const max = Math.max(...rows.map((r) => r.units), 1);
  return (
    <tr>
      <td
        colSpan={colSpan}
        style={{
          padding: "10px 16px 14px 34px",
          background: t.surface2,
          borderBottom: `1px solid ${t.line2}`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            marginBottom: 8,
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              color: t.ink4,
            }}
          >
            Forecast by channel · next 4 weeks
          </span>
          <span style={{ fontSize: 11.5, color: t.ink3 }}>
            {total.toLocaleString("en-US")} units across {rows.length}{" "}
            {rows.length === 1 ? "channel" : "channels"}
          </span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {rows.map((r) => (
            <div
              key={r.channel}
              style={{
                display: "grid",
                gridTemplateColumns: "160px minmax(0, 1fr) 70px 52px",
                alignItems: "center",
                gap: 12,
              }}
            >
              <span
                style={{
                  fontSize: 11.5,
                  color: t.ink2,
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {r.channel}
              </span>
              <div
                style={{
                  position: "relative",
                  height: 10,
                  background: t.line2,
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: (r.units / max) * 100 + "%",
                    background: t.greenInk,
                    opacity: 0.55,
                    borderRadius: 3,
                  }}
                />
              </div>
              <span
                style={{
                  fontSize: 11.5,
                  color: t.ink2,
                  textAlign: "right",
                  fontWeight: 600,
                }}
              >
                {r.units.toLocaleString("en-US")}
              </span>
              <span
                style={{ fontSize: 11, color: t.ink4, textAlign: "right" }}
              >
                {total > 0 ? Math.round((r.units / total) * 100) : 0}%
              </span>
            </div>
          ))}
        </div>
      </td>
    </tr>
  );
};

export const LowStockTable = ({
  rows,
  isHubAndSpoke,
  linkPath,
  linkLabel,
  showChannels,
}: {
  rows: SnapshotLowStockRow[];
  isHubAndSpoke: boolean;
  linkPath: string;
  linkLabel: string;
  showChannels: boolean;
}) => {
  const t = useSnapshotTokens();
  const td = useTd();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  // On hand and threshold add up across locations; days-to-stockout takes the
  // soonest, since that is when the SKU first hurts.
  const groups = groupBySku(rows)
    .map((g) => ({
      g,
      quantity: g.rows.reduce((a, r) => a + r.quantity, 0),
      threshold: g.rows.reduce(
        (a: number | null, r) =>
          r.threshold != null ? (a ?? 0) + r.threshold : a,
        null as number | null
      ),
      daysUntilStockout: Math.min(...g.rows.map((r) => r.daysUntilStockout)),
    }))
    .sort((a, b) => a.daysUntilStockout - b.daysUntilStockout);
  const shown = groups.slice(0, DISPLAY_CAP);
  const columns: { label: string; align?: "right" }[] = [
    { label: "SKU" },
    { label: "Locations" },
    { label: "On hand", align: "right" },
    { label: "Threshold", align: "right" },
    { label: "Soonest stockout", align: "right" },
  ];
  return (
    <RiskTable
      tone="orange"
      title="Low stock"
      count={rows.length}
      linkPath={linkPath}
      linkLabel={linkLabel}
      columns={columns}
    >
      {shown.map((a) => {
        const open = expanded.has(a.g.sku);
        return (
          <Fragment key={a.g.sku}>
            <tr>
              <SkuGroupCell
                group={a.g}
                open={open}
                onToggle={() => toggle(a.g.sku)}
              />
              <LocationCountCell group={a.g} />
              <td style={{ ...td, textAlign: "right" }}>{un(a.quantity)}</td>
              <td
                style={{ ...td, textAlign: "right", color: t.ink4 }}
                title={
                  a.threshold == null
                    ? "No demand at these locations, so no unit threshold"
                    : "Units needed to reach the target cover"
                }
              >
                {a.threshold == null ? EM_DASH : un(a.threshold)}
              </td>
              <td
                style={{
                  ...td,
                  textAlign: "right",
                  fontWeight: 600,
                  color: a.daysUntilStockout < 14 ? t.redInk : t.orangeInk,
                }}
              >
                {a.daysUntilStockout}d
              </td>
            </tr>
            {open && (
              <>
                <LocationBreakdownRow
                  group={a.g}
                  colSpan={columns.length}
                  isHubAndSpoke={isHubAndSpoke}
                  headers={[
                    { label: "On hand", align: "right" },
                    { label: "Threshold", align: "right" },
                    { label: "Days to stockout", align: "right" },
                  ]}
                  cells={(r) => (
                    <>
                      <DetailCell>{un(r.quantity)}</DetailCell>
                      <DetailCell>
                        {r.threshold == null ? EM_DASH : un(r.threshold)}
                      </DetailCell>
                      <DetailCell
                        bold
                        color={
                          r.daysUntilStockout < 14 ? t.redInk : t.orangeInk
                        }
                      >
                        {r.daysUntilStockout}d
                      </DetailCell>
                    </>
                  )}
                />
                {showChannels && (
                  <ChannelForecastPanel
                    rows={a.g.channels}
                    colSpan={columns.length}
                  />
                )}
              </>
            )}
          </Fragment>
        );
      })}
      <CapNote total={groups.length} sortedBy="days to stockout" />
      {!rows.length && (
        <tr>
          <td style={{ ...td, color: t.ink4 }} colSpan={columns.length}>
            No low-stock warnings in this run
          </td>
        </tr>
      )}
    </RiskTable>
  );
};

export const ExcessTable = ({
  rows,
  isHubAndSpoke,
  linkPath,
  showChannels,
}: {
  rows: SnapshotExcessRow[];
  isHubAndSpoke: boolean;
  linkPath: string;
  showChannels: boolean;
}) => {
  const t = useSnapshotTokens();
  const td = useTd();
  const valueTotal = rows.reduce(
    (a: number | null, r) => (r.value != null ? (a ?? 0) + r.value : a),
    null as number | null
  );
  const subtotal = valueTotal == null ? null : usd(valueTotal);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  // Quantity, value and demand add up across locations. Months of supply is a
  // ratio, so it is recomputed from the summed quantity and demand rather than
  // averaged — averaging ratios would weight a tiny location like a large one.
  const groups = groupBySku(rows)
    .map((g) => {
      const quantity = g.rows.reduce((a, r) => a + r.quantity, 0);
      const monthlyDemand = g.rows.reduce((a, r) => a + r.monthlyDemand, 0);
      return {
        g,
        quantity,
        monthlyDemand,
        value: g.rows.reduce(
          (a: number | null, r) => (r.value != null ? (a ?? 0) + r.value : a),
          null as number | null
        ),
        monthsOfSupply: monthlyDemand > 0 ? quantity / monthlyDemand : null,
      };
    })
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0) || b.quantity - a.quantity);
  const shown = groups.slice(0, DISPLAY_CAP);
  const columns: { label: string; align?: "right" }[] = [
    { label: "SKU" },
    { label: "Locations" },
    { label: "Qty", align: "right" },
    { label: "Value", align: "right" },
    { label: "MOS", align: "right" },
    { label: "Monthly demand", align: "right" },
  ];
  return (
    <RiskTable
      tone="orange"
      title="Excess inventory"
      count={rows.length}
      subtotal={
        rows.length && subtotal != null ? subtotal + " tied up" : undefined
      }
      linkPath={linkPath}
      linkLabel="Inventory"
      columns={columns}
    >
      {shown.map((a) => {
        const open = expanded.has(a.g.sku);
        return (
          <Fragment key={a.g.sku}>
            <tr>
              <SkuGroupCell
                group={a.g}
                open={open}
                onToggle={() => toggle(a.g.sku)}
              />
              <LocationCountCell group={a.g} />
              <td style={{ ...td, textAlign: "right" }}>{un(a.quantity)}</td>
              <td
                style={{ ...td, textAlign: "right", fontWeight: 600 }}
                title={
                  a.value == null
                    ? "Inventory $ value needs the run comparison summary"
                    : undefined
                }
              >
                {a.value == null ? EM_DASH : usd(a.value, 2)}
              </td>
              <td
                style={{
                  ...td,
                  textAlign: "right",
                  fontWeight: 600,
                  color: t.orangeInk,
                }}
                title={
                  a.monthsOfSupply == null
                    ? "No monthly demand for this SKU, so no months of supply"
                    : "Months of supply — summed quantity over summed demand"
                }
              >
                {a.monthsOfSupply == null
                  ? EM_DASH
                  : a.monthsOfSupply.toFixed(1)}
              </td>
              <td style={{ ...td, textAlign: "right" }}>
                {un(a.monthlyDemand)}
              </td>
            </tr>
            {open && (
              <>
                <LocationBreakdownRow
                  group={a.g}
                  colSpan={columns.length}
                  isHubAndSpoke={isHubAndSpoke}
                  headers={[
                    { label: "Qty", align: "right" },
                    { label: "Value", align: "right" },
                    { label: "MOS", align: "right" },
                    { label: "Monthly demand", align: "right" },
                  ]}
                  cells={(r) => (
                    <>
                      <DetailCell>{un(r.quantity)}</DetailCell>
                      <DetailCell>
                        {r.value == null ? EM_DASH : usd(r.value, 2)}
                      </DetailCell>
                      <DetailCell bold color={t.orangeInk}>
                        {r.monthsOfSupply.toFixed(1)}
                      </DetailCell>
                      <DetailCell>{un(r.monthlyDemand)}</DetailCell>
                    </>
                  )}
                />
                {showChannels && (
                  <ChannelForecastPanel
                    rows={a.g.channels}
                    colSpan={columns.length}
                  />
                )}
              </>
            )}
          </Fragment>
        );
      })}
      <CapNote total={groups.length} sortedBy="value, then quantity" />
      {!rows.length && (
        <tr>
          <td style={{ ...td, color: t.ink4 }} colSpan={columns.length}>
            No excess inventory in this run
          </td>
        </tr>
      )}
    </RiskTable>
  );
};

export const RiskStack = ({
  stockOuts,
  lowStock,
  excess,
  isHubAndSpoke,
  inventoryPath,
  inventoryHealthPath,
  transferPlanningPath,
  showChannels,
}: {
  stockOuts: SnapshotStockOutRow[];
  lowStock: SnapshotLowStockRow[];
  excess: SnapshotExcessRow[];
  isHubAndSpoke: boolean;
  inventoryPath: string;
  inventoryHealthPath: string;
  transferPlanningPath: string;
  /** False for single-channel customers, where a per-channel split of a SKU's
   *  demand is just the SKU's demand again and only adds noise. */
  showChannels: boolean;
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
    <SectionTitle note="Every flagged SKU in this run — open one for its locations">
      Risks
    </SectionTitle>
    <StockOutTable
      rows={stockOuts}
      isHubAndSpoke={isHubAndSpoke}
      linkPath={inventoryHealthPath}
      showChannels={showChannels}
    />
    <LowStockTable
      rows={lowStock}
      isHubAndSpoke={isHubAndSpoke}
      linkPath={isHubAndSpoke ? transferPlanningPath : inventoryHealthPath}
      linkLabel={isHubAndSpoke ? "Transfer Planning" : "Inventory Health"}
      showChannels={showChannels}
    />
    <ExcessTable
      rows={excess}
      isHubAndSpoke={isHubAndSpoke}
      linkPath={inventoryPath}
      showChannels={showChannels}
    />
  </div>
);
